import {
  IconBriefcase,
  IconNotebook,
  IconCode,
  IconDatabase,
  IconRefresh,
  IconTerminal,
} from "@tabler/icons-react";
import { EndpointCallerItem, AIFunctionUsageItem } from "../types";

type UsageType = "MV Refresh" | "Job" | "Notebook" | "SQL Query" | "DDL";
type CallerType = "job" | "notebook";

const USAGE_TYPE_CONFIG = {
  "MV Refresh": {
    icon: IconRefresh,
    color: "al-text-green-600",
    bg: "al-bg-green-100",
    text: "al-text-green-800",
  },
  Job: {
    icon: IconBriefcase,
    color: "al-text-blue-600",
    bg: "al-bg-blue-100",
    text: "al-text-blue-800",
  },
  Notebook: {
    icon: IconNotebook,
    color: "al-text-purple-600",
    bg: "al-bg-purple-100",
    text: "al-text-purple-800",
  },
  "SQL Query": {
    icon: IconDatabase,
    color: "al-text-orange-600",
    bg: "al-bg-orange-100",
    text: "al-text-orange-800",
  },
  DDL: {
    icon: IconCode,
    color: "al-text-gray-600",
    bg: "al-bg-gray-100",
    text: "al-text-gray-800",
  },
} as const;

const DEFAULT_USAGE_TYPE_CONFIG = {
  icon: IconTerminal,
  color: "al-text-gray-500",
  bg: "al-bg-gray-100",
  text: "al-text-gray-700",
} as const;

export const getUsageTypeConfig = (usageType: UsageType | string) =>
  USAGE_TYPE_CONFIG[usageType as UsageType] ?? DEFAULT_USAGE_TYPE_CONFIG;

const CALLER_TYPE_CONFIG = {
  job: {
    icon: IconBriefcase,
    label: "Job",
    color: "al-text-blue-600",
    bg: "al-bg-blue-100",
    text: "al-text-blue-800",
  },
  notebook: {
    icon: IconNotebook,
    label: "Notebook",
    color: "al-text-purple-600",
    bg: "al-bg-purple-100",
    text: "al-text-purple-800",
  },
} as const;

const DEFAULT_CALLER_TYPE_CONFIG = {
  icon: IconCode,
  label: "Query",
  color: "al-text-gray-500",
  bg: "al-bg-gray-100",
  text: "al-text-gray-700",
} as const;

export const getCallerTypeConfig = (callerType: CallerType | string) =>
  CALLER_TYPE_CONFIG[callerType as CallerType] ?? DEFAULT_CALLER_TYPE_CONFIG;

const CALLER_IDENTITY_MAP: Record<
  string,
  (caller: EndpointCallerItem) => { name: string; subtitle: string | null }
> = {
  job: caller => ({
    name: caller.job_name || `Job ${caller.job_id}`,
    subtitle: caller.job_id ? `Job ID: ${caller.job_id}` : null,
  }),
  notebook: caller => ({
    name: `Notebook ${caller.notebook_id}`,
    subtitle: caller.notebook_id ? `Notebook ID: ${caller.notebook_id}` : null,
  }),
};

export const getCallerIdentity = (caller: EndpointCallerItem) =>
  CALLER_IDENTITY_MAP[caller.caller_type]?.(caller) ?? {
    name: caller.executed_by || "Direct Query",
    subtitle: null,
  };

const AI_FUNCTION_IDENTITY_MAP: Record<
  string,
  (item: AIFunctionUsageItem) => { name: string; subtitle: string | null }
> = {
  "MV Refresh": item => ({
    name: item.usage_identity,
    subtitle: item.source_identifier ? `Statement: ${item.source_identifier}` : null,
  }),
  DDL: item => ({
    name: item.usage_identity,
    subtitle: item.source_identifier ? `Statement: ${item.source_identifier}` : null,
  }),
  Job: item => ({
    name: item.job_name || `Job ${item.usage_identity}`,
    subtitle: `Job ID: ${item.usage_identity}`,
  }),
  Notebook: item => ({
    name: `Notebook ${item.usage_identity}`,
    subtitle: `Notebook ID: ${item.usage_identity}`,
  }),
  "SQL Query": item => ({
    name: `SQL Query ${item.usage_identity}`,
    subtitle: `Query ID: ${item.usage_identity}`,
  }),
};

export const getAIFunctionIdentity = (item: AIFunctionUsageItem) =>
  AI_FUNCTION_IDENTITY_MAP[item.usage_type]?.(item) ?? {
    name: "Ad-hoc (SQL Editor)",
    subtitle: item.executed_by
      ? `User: ${item.executed_by}`
      : item.source_identifier
        ? `Statement: ${item.source_identifier}`
        : null,
  };

const USAGE_TYPE_COLOR_MAP: Record<string, string> = {
  "MV Refresh": "green",
  Job: "blue",
  Notebook: "purple",
  "SQL Query": "orange",
  DDL: "blue",
};

export const getUsageTypeColor = (usageType: UsageType | string): string =>
  USAGE_TYPE_COLOR_MAP[usageType] ?? "blue";

const CALLER_TYPE_COLOR_MAP: Record<string, string> = {
  job: "blue",
  notebook: "purple",
};

export const getCallerTypeColor = (callerType: CallerType | string): string =>
  CALLER_TYPE_COLOR_MAP[callerType] ?? "blue";
